import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';

import { SearchField } from './parts';
import { tx } from './i18n';
import { errorMessage } from './ui';

// The Google address search: suggestions as you type, and the chosen place's
// structured address (lines, postcode, city, region, country, coordinates).
// The caller passes the lookups its staff may use.
//
// With `onType` the box is a form's own address line: what is typed in it is
// the line (the caller is told as it is typed, and says what the line reads in
// `value`), and a chosen suggestion fills the caller's form. `inputId` names
// the box for the form's label. The caller is handed the chosen place and the
// suggestion it was chosen from (which says a flat typed in front of it).
export default function AddressPicker({
  value, onSelect, disabled, search, details, onType = null, inputId,
}) {
  const isLine = onType !== null;
  const [query, setQuery] = useState(value || '');
  // In a form's address line, what was typed since the last choice is searched
  // for. Leaving the line closes its suggestions, a moment later so that one
  // can still be chosen; coming straight back keeps them.
  const [typed, setTyped] = useState(false);
  const leaving = useRef(0);
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const resultsRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  // In a scrolling form the suggestions can open below its fold; bring them into view.
  useEffect(() => {
    if (suggestions.length > 0) resultsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [suggestions]);

  useEffect(() => {
    const trimmed = query.trim();
    if (disabled || trimmed.length < 3 || (isLine ? !typed : trimmed === value)) {
      setSuggestions([]);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setBusy(true);
      setError('');
      try {
        const next = await search(trimmed);
        if (active) setSuggestions(next);
      } catch (requestError) {
        if (active) setError(errorMessage(requestError));
      } finally {
        if (active) setBusy(false);
      }
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [disabled, isLine, query, search, typed, value]);

  function change(text) {
    setQuery(text);
    if (isLine) { setTyped(true); onType(text); }
  }

  async function select(suggestion) {
    setBusy(true);
    setError('');
    try {
      const address = await details(suggestion.placeId);
      if (isLine) setTyped(false); else setQuery(address.formattedAddress || suggestion.description);
      setSuggestions([]);
      onSelect(address, suggestion);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="xo-location-place-picker">
      <SearchField id={inputId} value={query} disabled={disabled} onChange={change}
        placeholder={tx('ui.locations.search_address')}
        label={tx('ui.locations.search_address')}
        onFocus={isLine ? () => window.clearTimeout(leaving.current) : undefined}
        onBlur={isLine ? () => { leaving.current = window.setTimeout(() => setTyped(false), 200); } : undefined} />
      {busy && <span className="xo-location-place-busy">{tx('ui.locations.searching')}</span>}
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      {suggestions.length > 0 && (
        <div ref={resultsRef} className="xo-location-place-results xo-search-results">
          {suggestions.map((suggestion) => (
            <button type="button" key={suggestion.placeId}
              onMouseDown={isLine ? (event) => event.preventDefault() : undefined}
              onClick={() => select(suggestion)}>
              <MapPin aria-hidden="true" /><span>{suggestion.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
